import React, { useState, useEffect, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArticleDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
} from '../../../components/staff/articles/ArticleDesk';
import ConfirmModal from '../../../components/ConfirmModal';

function ShareDraftModal({ article, onClose }) {
    const [collaborators, setCollaborators] = useState([]);
    const [habboName, setHabboName] = useState('');
    const [loading, setLoading] = useState(true);
    const [sharing, setSharing] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const inputRef = useRef(null);
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        loadCollaborators();
        setTimeout(() => inputRef.current?.focus(), 50);
    }, []);

    const loadCollaborators = async () => {
        try {
            const res = await fetch(`${apiUrl}articles/author/articles/${article.id}/collaborators`, {
                credentials: 'include'
            });
            const data = await res.json();
            if (data.success) setCollaborators(data.collaborators);
        } catch {
            toast.error('Failed to load collaborators');
        } finally {
            setLoading(false);
        }
    };

    const handleShare = async (e) => {
        e.preventDefault();
        if (!habboName.trim()) return;
        try {
            setSharing(true);
            const res = await fetch(`${apiUrl}articles/author/articles/${article.id}/collaborators`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ habboName: habboName.trim() })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                setHabboName('');
                loadCollaborators();
            } else {
                toast.error(data.message || 'Failed to share draft');
            }
        } catch {
            toast.error('Failed to share draft');
        } finally {
            setSharing(false);
        }
    };

    const handleRemove = async (collaboratorId, name) => {
        try {
            setRemovingId(collaboratorId);
            const res = await fetch(
                `${apiUrl}articles/author/articles/${article.id}/collaborators/${collaboratorId}`,
                { method: 'DELETE', credentials: 'include' }
            );
            const data = await res.json();
            if (data.success) {
                toast.success(`Removed ${name}`);
                setCollaborators(prev => prev.filter(c => c.id !== collaboratorId));
            } else {
                toast.error(data.message || 'Failed to remove collaborator');
            }
        } catch {
            toast.error('Failed to remove collaborator');
        } finally {
            setRemovingId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white w-full max-w-md shadow-xl">
                <div className="px-6 pt-6 pb-4 border-b border-gray-200">
                    <h2 className="font-['Jersey_10'] uppercase text-[20px] tracking-[1px] text-coal_dark">
                        Share Draft
                    </h2>
                    <p className="text-[12px] text-gray-500 mt-1 truncate">{article.title}</p>
                </div>

                <div className="px-6 py-4">
                    <form onSubmit={handleShare} className="flex gap-2 mb-5">
                        <input
                            ref={inputRef}
                            type="text"
                            value={habboName}
                            onChange={(e) => setHabboName(e.target.value)}
                            placeholder="Enter Habbo name..."
                            className="flex-1 px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <button
                            type="submit"
                            disabled={sharing || !habboName.trim()}
                            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] transition-colors"
                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                        >
                            {sharing ? '...' : 'Share'}
                        </button>
                    </form>

                    <p className="font-['Jersey_10'] uppercase text-[12px] tracking-[1px] text-gray-500 mb-2">
                        Current collaborators
                    </p>

                    {loading ? (
                        <p className="text-sm text-gray-400">Loading...</p>
                    ) : collaborators.length === 0 ? (
                        <p className="text-sm text-gray-400">No one has access yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {collaborators.map(c => (
                                <li key={c.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 px-3 py-2">
                                    <span className="text-sm font-medium text-coal_dark">{c.habboName}</span>
                                    <button
                                        onClick={() => handleRemove(c.id, c.habboName)}
                                        disabled={removingId === c.id}
                                        className="text-red-600 hover:text-red-800 disabled:opacity-50 text-[11px] font-['Jersey_10'] uppercase tracking-[1px] transition-colors"
                                    >
                                        {removingId === c.id ? '...' : 'Remove'}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="px-6 pb-5 flex justify-end">
                    <button
                        onClick={onClose}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-5 py-2 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

function MyArticles({ userData }) {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [publishingId, setPublishingId] = useState(null);
    const [shareModal, setShareModal] = useState(null);

    useEffect(() => {
        loadArticles();
    }, []);

    if (!userData || !userData.hasStaffRole) {
        return <Navigate to="/" replace />;
    }

    const loadArticles = async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/author/articles`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setArticles(data.articles);
            } else {
                toast.error(data.message || 'Failed to load articles');
            }
        } catch (error) {
            console.error('Error loading articles:', error);
            toast.error('Failed to load articles');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (article) => {
        navigate(`/staff/articles/edit/${article.id}`, { state: { article } });
    };

    const handleDeleteClick = (article) => {
        setDeleteConfirm(article);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm) return;
        try {
            setDeleting(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/author/articles/${deleteConfirm.id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Article deleted successfully');
                setArticles(articles.filter(a => a.id !== deleteConfirm.id));
                setDeleteConfirm(null);
            } else {
                toast.error(data.message || 'Failed to delete article');
            }
        } catch (error) {
            console.error('Error deleting article:', error);
            toast.error('Failed to delete article');
        } finally {
            setDeleting(false);
        }
    };

    const handlePublishDraft = async (article) => {
        try {
            setPublishingId(article.id);
            const apiUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${apiUrl}articles/author/articles/${article.id}/publish`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Article published successfully!');
                setArticles(prev =>
                    prev.map(a => a.id === article.id ? { ...a, draft: false } : a)
                );
            } else {
                toast.error(data.message || 'Failed to publish article');
            }
        } catch (error) {
            console.error('Error publishing article:', error);
            toast.error('Failed to publish article');
        } finally {
            setPublishingId(null);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const drafts = articles.filter(a => a.draft).length;

    const actionButton = (colour) =>
        `${colour} px-2 py-1 font-['Jersey_10'] text-[11px] uppercase tracking-[1px] text-white transition-colors disabled:opacity-50`;

    return (
        <ArticleDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>My Articles</DeskTitle>
                <DeskNote>
                    {articles.length} article{articles.length !== 1 ? 's' : ''}
                    {drafts > 0 ? ` · ${drafts} draft${drafts !== 1 ? 's' : ''}` : ''}
                </DeskNote>

                <div className="flex-1" />

                <Link
                    to="/staff/articles/new"
                    className="px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-opacity hover:opacity-90"
                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)', backgroundColor: '#5A2929' }}
                >
                    New Article
                </Link>
            </DeskRibbon>

            <DeskPanel padding="p-0">
                {loading ? (
                    <div className="space-y-2 p-3">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="h-12 animate-pulse bg-gray-100" />
                        ))}
                    </div>
                ) : articles.length === 0 ? (
                    <p className="py-10 text-center text-sm uppercase text-gray-500">
                        You have not written any articles yet.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-100">
                                    <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Title</th>
                                    <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] md:table-cell">Category</th>
                                    <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] sm:table-cell">Date</th>
                                    <th className="px-3 py-2 text-right font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {articles.map(article => (
                                    <tr
                                        key={article.id}
                                        className={`border-b border-gray-200 transition-colors ${
                                            article.draft ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <td className="px-3 py-3">
                                            <div className="flex flex-wrap items-center gap-2">
                                                {article.draft ? (
                                                    <span className="shrink-0 border border-yellow-300 bg-yellow-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-yellow-700">
                                                        Draft
                                                    </span>
                                                ) : (
                                                    <span className="shrink-0 border border-green-300 bg-green-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-green-700">
                                                        Live
                                                    </span>
                                                )}
                                                <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-coal_dark">
                                                    {article.title}
                                                </span>
                                                {article.isSharedWithMe && (
                                                    <span className="shrink-0 bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-blue-700">
                                                        Shared by {article.authorName}
                                                    </span>
                                                )}
                                                {article.spotlight && !article.draft && (
                                                    <span className="shrink-0 text-[10px] font-bold uppercase text-maroon">Spotlight</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="hidden px-3 py-3 text-[12px] text-gray-600 md:table-cell">
                                            {article.categoryName}
                                        </td>
                                        <td className="hidden whitespace-nowrap px-3 py-3 text-[12px] text-gray-500 sm:table-cell">
                                            {formatDate(article.publicDate)}
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="flex flex-wrap justify-end gap-1">
                                                {article.draft && !article.isSharedWithMe && (
                                                    <button
                                                        onClick={() => handlePublishDraft(article)}
                                                        disabled={publishingId === article.id}
                                                        className={actionButton('bg-green-600 hover:bg-green-700')}
                                                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                                    >
                                                        {publishingId === article.id ? '...' : 'Publish'}
                                                    </button>
                                                )}
                                                {article.draft && !article.isSharedWithMe && (
                                                    <button
                                                        onClick={() => setShareModal(article)}
                                                        className={actionButton('bg-purple-600 hover:bg-purple-700')}
                                                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                                    >
                                                        Share
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(article)}
                                                    className={actionButton('bg-blue-600 hover:bg-blue-700')}
                                                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                                >
                                                    Edit
                                                </button>
                                                {!article.isSharedWithMe && (
                                                    <button
                                                        onClick={() => handleDeleteClick(article)}
                                                        className={actionButton('bg-red-600 hover:bg-red-700')}
                                                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DeskPanel>

            <ConfirmModal
                isOpen={!!deleteConfirm}
                title="Delete Article"
                message={`Are you sure you want to delete "${deleteConfirm?.title}"? This action cannot be undone.`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteConfirm(null)}
                confirmText="Delete"
                confirmColor="bg-red-600 hover:bg-red-700"
                isLoading={deleting}
            />

            {shareModal && (
                <ShareDraftModal
                    article={shareModal}
                    onClose={() => setShareModal(null)}
                />
            )}
        </ArticleDesk>
    );
}

export default MyArticles;
